/**
 * @module createOpenGraph
 */
const {
  convertS3DamItems,
  replaceMagnoliaAssetsToImgix,
} = require('../../utils/helpers/magnolia/imgix');
const { addParamsToUrl } = require('../../utils/helpers/magnolia/urls');

/**
 * @typedef {object} MgnlEnvVars
 * @property {string} IMGIX_BASE_URL -Imgix base url (e.g. 'https://cms-images.life.church').
 */

/**
 * @typedef {object} OgImageParams
 * @property {MgnlEnvVars} ogImageParams.mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use process.env.
 * @property {string} [ogImageParams.facebook] - Optional image url for Facebook. (Default: /open-graph-images/default-fb-open-graph.jpg).
 * @property {string} [ogImageParams.twitter] - Optional image url for Twitter. (Default: /open-graph-images/default-tw-open-graph.jpg).
 */

/**
 * @typedef {object} OpenGraphOptions
 * @property {boolean} convertS3Items - Boolean flag denoting whether or not to convert s3 dam items (Default: true).
 * @property {boolean} includeKeywords - Optional boolean flag denoting whether or not to include the keywords meta tag (Default: true).
 * @property {boolean} includeOgAppId - Optional boolean flag denoting whether or not to include the og:app_id meta tag (Default: false).
 * @property {boolean} titleOverride - Optional title override value.
 */

/**
 * @typedef {object} OpenGraphParams
 * @property {MgnlEnvVars} openGraphParams.mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use process.env.
 * @property {OpenGraphOptions} [openGraphParams.options] - Optional function params.
 * @property {object} openGraphParams.req - The request object.
 * @property {object} openGraphParams.res - The response object.
 * @property {object} [openGraphParams.scriptMarkups] - Optional object of additional script markup data to inject into the page markup. Note that this is a free data object that will be iterated over, the values of each key/value pair will be injected at the end of the returned markup.
 */

/**
 * Convenience function to retrieve and return Facebook and Twitter image values for open graph.
 *
 * @param {OgImageParams} ogImageParams - The function params object.
 * @param {MgnlEnvVars} ogImageParams.mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 * @param {string} [ogImageParams.facebook] - Optional image url for Facebook. (Default: /open-graph-images/default-fb-open-graph.jpg).
 * @param {string} [ogImageParams.twitter] - Optional image url for Twitter. (Default: /open-graph-images/default-tw-open-graph.jpg).
 *
 * @returns {Array} Two-item array of [fbImage, twImage].
 */
/* istanbul ignore next */
const getOGImages = async ({ facebook, mgnlEnvVars, twitter }) => {
  const envVars = mgnlEnvVars || process.env;
  const defaultFbImage = `${envVars.IMGIX_BASE_URL}/open-graph-images/default-fb-open-graph.jpg`;
  const defaultTwImage = `${envVars.IMGIX_BASE_URL}/open-graph-images/default-tw-open-graph.jpg`;
  const fbImage = facebook || defaultFbImage;
  const twImage = twitter || defaultTwImage;
  const imgs = [];
  /* istanbul ignore next */
  imgs[0] = await replaceMagnoliaAssetsToImgix(fbImage, envVars.IMGIX_BASE_URL);
  imgs[1] = await replaceMagnoliaAssetsToImgix(twImage, envVars.IMGIX_BASE_URL);
  imgs[0] = addParamsToUrl(imgs[0], `w=1200&amp;h=630&amp;fit=crop`);
  imgs[1] = addParamsToUrl(imgs[1], `w=760&amp;h=560&amp;fit=crop`);
  return imgs;
};

/**
 * Convenience function to create and return open graph markup for page rendering.
 *
 * @param {OpenGraphParams} openGraphParams - The function params object.
 * @param {MgnlEnvVars} openGraphParams.mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 * @param {OpenGraphOptions} [openGraphParams.options] - Optional function params.
 * @param {object} openGraphParams.req - The request object.
 * @param {object} openGraphParams.res - The response object.
 * @param {object} [openGraphParams.scriptMarkups] - Optional object of additional script markup data to inject into the page markup. Note that this is a free data object that will be iterated over, the values of each key/value pair will be injected at the end of the returned markup.
 *
 * @returns {string} HTML markup string of open graph meta tags.
 */
const createOpenGraph = async ({
  mgnlEnvVars,
  options = {
    convertS3Items: true,
    includeKeywords: true,
    includeOgAppId: false,
    titleOverride: 'Life.Church',
  },
  req,
  res,
  scriptMarkups,
}) => {
  const envVars = mgnlEnvVars || process.env;

  /* istanbul ignore next */
  let currentPage = options.convertS3Items
    ? convertS3DamItems(
        res && res.pagedata && res.pagedata.currentPage
          ? res.pagedata.currentPage
          : null,
      )
    : res && res.pagedata && res.pagedata.currentPage
    ? res.pagedata.currentPage
    : null;
  /* istanbul ignore next */
  if (!currentPage) {
    currentPage = {};
  }
  const {
    canonical = false,
    componentProps = {},
    description = '',
    facebook_og_image = '',
    noFollow = false,
    noIndex = false,
    ogTitle = '',
    seoDescription = '',
    seoKeywords = '',
    title = options && options.titleOverride
      ? options.titleOverride
      : 'Life.Church',
    twitter_og_image = '',
  } = currentPage;

  /**
   * Determine if page is a location page. Note that while not desirable,
   * without the `!!`, this resolves not to a boolean but rather to the value
   * of the `name` attribute. While this "works", it's preferable to have it
   * resolve to a boolean, and using `!!` does this. Additionally, set location-
   * specific values to be used conditionally for meta tag values.
   */
  const isLocation =
    !!componentProps &&
    componentProps.name &&
    componentProps.primaryAddress &&
    componentProps.primaryAddress.street1 &&
    componentProps.slug &&
    componentProps.primaryAddress.state;
  const locationDescription = `Life.Church is passionate about sharing the love of Christ by caring for each other and serving our ${
    componentProps && componentProps.name ? componentProps.name.trim() : ''
  } community.`;
  const locationOgImages = {
    fbImage: isLocation
      ? await replaceMagnoliaAssetsToImgix(
          `${envVars.IMGIX_BASE_URL}/open-graph-images/default-fb-open-graph-join-me.jpg`,
          envVars.IMGIX_BASE_URL,
        )
      : '',
    twImage: isLocation
      ? await replaceMagnoliaAssetsToImgix(
          `${envVars.IMGIX_BASE_URL}/open-graph-images/default-tw-open-graph-join-me.jpg`,
          envVars.IMGIX_BASE_URL,
        )
      : null,
  };
  locationOgImages.fbImage = isLocation
    ? addParamsToUrl(locationOgImages.fbImage, `w=1200&amp;h=630&amp;fit=crop`)
    : '';
  locationOgImages.twImage = isLocation
    ? addParamsToUrl(locationOgImages.twImage, `w=760&amp;h=560&amp;fit=crop`)
    : '';
  const defaultKeywords =
    'church, church online, life church, lifechurch, life.church, messages, Craig Groeschel';

  /* istanbul ignore next */
  const protocol = envVars.NODE_ENV === 'development' ? 'http' : 'https';
  /* istanbul ignore next */
  let fullURL = isLocation
    ? `https://www.life.church/${
        componentProps && componentProps.slug ? componentProps.slug : ''
      }/`
    : `${protocol}://${req.get('host')}${req.originalUrl.split('?')[0]}`;
  /* istanbul ignore next */
  if (canonical) {
    fullURL = `${protocol}://${req.get('host')}${canonical}`;
  }
  /* istanbul ignore next */
  const [fbImage, twImage] = await getOGImages({
    facebook: facebook_og_image,
    mgnlEnvVars: envVars,
    twitter: twitter_og_image,
  });
  let adjustedTitle =
    options && options.titleOverride && title.indexOf(options.titleOverride) < 0
      ? `${title} | ${options.titleOverride}`
      : title;
  if (isLocation) {
    adjustedTitle = `Life.Church ${componentProps.name}`.trim();
  }
  let adjustedDescription = description || seoDescription;
  if (isLocation) {
    adjustedDescription = locationDescription;
  }
  let adjustedKeywords = seoKeywords || defaultKeywords;
  if (isLocation) {
    adjustedKeywords = `${adjustedKeywords}, ${componentProps.name.trim()}, ${
      componentProps.primaryAddress.state
    }`;
  }
  const keywordsMarkup =
    options && options.includeKeywords
      ? `<meta name="keywords" content="${adjustedKeywords}" />`
      : '';

  let robotsMetaContent = '';
  if (noIndex) {
    robotsMetaContent = 'noindex';
  }
  if (noFollow) {
    robotsMetaContent = robotsMetaContent
      ? `${robotsMetaContent}, nofollow`
      : 'nofollow';
  }
  const robotsMetaTag = robotsMetaContent
    ? `<meta name="robots" content="${robotsMetaContent}">`
    : '';

  const additionalMetaTags = isLocation
    ? `<meta property="place:location:latitude" content="${
        componentProps.primaryAddress.latitude
      }" />
    <meta property="place:location:longitude" content="${
      componentProps.primaryAddress.longitude
    }" />
    <meta property="fb:profile_id" content="${
      componentProps && componentProps.facebook ? componentProps.facebook : ''
    }" />`
    : '';

  let additionalScriptsMarkup = '';
  if (scriptMarkups && Object.keys(scriptMarkups).length) {
    Object.values(scriptMarkups).forEach((markup) => {
      additionalScriptsMarkup = `${additionalScriptsMarkup}\r\n${markup}`;
    });
  }

  const openGraphMarkup = `<title>${adjustedTitle}</title>
  ${robotsMetaTag}
  <meta name="description" content="${adjustedDescription}" />
  ${keywordsMarkup}
  <link rel="canonical" href="${fullURL}" />
  <meta property="og:title" content="${ogTitle || adjustedTitle}" />
  <meta property="og:description" content="${adjustedDescription}" />
  <meta property="og:type" content="${isLocation ? 'place' : 'article'}" />
  <meta property="og:url" content="${fullURL}" />
  ${
    options && options.includeOgAppId
      ? '<meta property="og:app_id" content="567912296616576" />'
      : ''
  }
  <meta property="og:image" content="${
    isLocation ? locationOgImages.fbImage : fbImage
  }" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  ${additionalMetaTags}
  <meta name="twitter:image" content="${
    isLocation ? locationOgImages.twImage : twImage
  }" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:site" content="@lifechurch" />
  <script>
    window.dataLayer = window.dataLayer ||[];
    function gtag(){dataLayer.push(arguments);}
    gtag('consent','default',{
      'ad_storage':'denied',
      'ad_personalization':'denied',
      'ad_user_data':'denied',
      'analytics_storage':'denied',
      'functionality_storage':'granted',
      'personalization_storage':'denied',
      'security_storage':'granted',
      'wait_for_update': 500
    });
    gtag("set", "ads_data_redaction", true);
  </script>
  ${additionalScriptsMarkup}
  `;

  return openGraphMarkup;
};

module.exports = { createOpenGraph, getOGImages };
